/**
 * Notification State Service
 *
 * Maintains notification count state across navigation and component lifecycle
 */

import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, timer, forkJoin, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { CourseInvitationService } from './course-invitation.service';
import { NotificationsApiService } from './notifications-api.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationStateService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private notificationCountSubject = new BehaviorSubject<number>(0);

  // Public observable for components to subscribe to
  notificationCount$ = this.notificationCountSubject.asObservable();

  private readonly BASE_INTERVAL_MS = 120000; // 2 minutes
  private readonly MAX_INTERVAL_MS = 600000; // 10 minutes
  private currentIntervalMs = this.BASE_INTERVAL_MS;
  private consecutiveErrors = 0;
  private pollTimerSub: Subscription | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor(
    private invitationService: CourseInvitationService,
    private notificationsApi: NotificationsApiService
  ) {
    this.startPolling();
    this.setupVisibilityListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
    this.removeVisibilityListener();
  }

  private setupVisibilityListener(): void {
    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stopPolling();
      } else {
        this.resetBackoff();
        this.startPolling();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private removeVisibilityListener(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private startPolling(): void {
    this.stopPolling();
    this.fetchNotifications(); // Immediate fetch
    this.scheduleNextPoll();
  }

  private stopPolling(): void {
    if (this.pollTimerSub) {
      this.pollTimerSub.unsubscribe();
      this.pollTimerSub = null;
    }
  }

  private scheduleNextPoll(): void {
    this.pollTimerSub = timer(this.currentIntervalMs)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchNotifications();
        this.scheduleNextPoll();
      });
  }

  private fetchNotifications(): void {
    // KAN-330: the bell badge now reflects BOTH pending invitations AND unread
    // in-app notifications (e.g. unit completions). forkJoin so one failing
    // source degrades to 0 for that source instead of zeroing the whole badge.
    forkJoin({
      invitations: this.invitationService.getInvitationStats().pipe(
        map((stats) => stats.pendingReceived),
        catchError(() => of(0))
      ),
      unread: this.notificationsApi.getUnreadCount().pipe(catchError(() => of(0)))
    }).subscribe({
      next: ({ invitations, unread }) => {
        this.notificationCountSubject.next((invitations || 0) + (unread || 0));
        this.resetBackoff();
      },
      error: (error) => {
        console.error('Error loading notification count:', error);
        this.notificationCountSubject.next(0);
        this.applyBackoff();
      }
    });
  }

  private resetBackoff(): void {
    this.consecutiveErrors = 0;
    this.currentIntervalMs = this.BASE_INTERVAL_MS;
  }

  private applyBackoff(): void {
    this.consecutiveErrors++;
    this.currentIntervalMs = Math.min(
      this.BASE_INTERVAL_MS * Math.pow(2, this.consecutiveErrors),
      this.MAX_INTERVAL_MS
    );
  }

  // Method to manually refresh the count (useful after sending/cancelling
  // invitations OR marking notifications read).
  refreshCount(): void {
    forkJoin({
      invitations: this.invitationService.getInvitationStats().pipe(
        map((stats) => stats.pendingReceived),
        catchError(() => of(0))
      ),
      unread: this.notificationsApi.getUnreadCount().pipe(catchError(() => of(0)))
    }).subscribe({
      next: ({ invitations, unread }) => {
        this.notificationCountSubject.next((invitations || 0) + (unread || 0));
      },
      error: () => {
        this.notificationCountSubject.next(0);
      }
    });
  }
}
